import React, { useEffect, useState } from "react";
import {
	Grid,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	Button,
	RadioGroup,
	FormControlLabel,
	Radio,
	Paper,
	Box,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { sickleAPI } from "../../../services/sickle.api";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Loader from "../../../components/Loader";

// ─── Validation Schema ───────────────────────────────────────────────────────
const validationSchema = Yup.object().shape({
	MobileNo: Yup.string()
		.matches(/^[6-9]\d{9}$/, "Contact No. must start with 6-9 and be exactly 10 digits")
		.required("Mobile Number is required"),
	NameOfPerson: Yup.string().required("Patient Name is required"),
	Email: Yup.string().email("Invalid email").nullable(),
	Gender: Yup.string().required("Gender is required"),
	age_dob: Yup.string().required("Please select DOB or Age"),

	dob: Yup.string().when("age_dob", {
		is: "dob",
		then: () => Yup.string().required("DOB is required"),
		otherwise: () => Yup.string().nullable(),
	}),

	Year: Yup.number().when("age_dob", {
		is: "age",
		then: () =>
			Yup.number()
				.typeError("Year must be a number")
				.required("Year is required")
				.min(1900, "Year must be a valid year and at least 1900"),
		otherwise: () => Yup.number().nullable(),
	}),

	month: Yup.number().when("age_dob", {
		is: "age",
		then: () =>
			Yup.number()
				.typeError("Month must be a number")
				.required("Month is required")
				.min(1, "Month must be a positive number")
				.max(12, "Month must be between 1 and 12"),
		otherwise: () => Yup.number().nullable(),
	}),

	day: Yup.number().when("age_dob", {
		is: "age",
		then: () =>
			Yup.number()
				.typeError("Day must be a number")
				.required("Day is required")
				.min(1, "Day must be a positive number")
				.max(31, "Day must be between 1 and 31"),
		otherwise: () => Yup.number().nullable(),
	}),

	block_id: Yup.number().when("is_rural_urban", {
		is: "R",
		then: () => Yup.number().typeError("Block is required").required("Block is required"),
		otherwise: () => Yup.number().nullable(),
	}),

	village_id: Yup.number().when("is_rural_urban", {
		is: "R",
		then: () => Yup.number().typeError("Village is required").required("Village is required"),
		otherwise: () => Yup.number().nullable(),
	}),

	urban_id: Yup.number().when("is_rural_urban", {
		is: "U",
		then: () => Yup.number().typeError("Town/Municipality is required").required("Town/Municipality is required"),
		otherwise: () => Yup.number().nullable(),
	}),

	MaritalStatus: Yup.string().required("Marital Status is required"),
	SonDaughterOfWifeOf: Yup.string().required("Father/Husband Name is required"),
	category: Yup.string().required("Category is required"),
	BloodGroupID: Yup.string().required("Blood Group is required"),
	Address: Yup.string().required("Address is required"),
	state_id: Yup.string().nullable().required("State is required"),
	district_id: Yup.string().nullable().required("District is required"),
	is_rural_urban: Yup.string().nullable().required("Rural / Urban is required"),
	registration_date: Yup.string().required("Registration Date is required"),
});

export const SickleRegistration = ({ targetRef, onClose }) => {
	// Master data state
	const [masterData, setMasterData] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [isAbhaVerified, setIsAbhaVerified] = useState(false);

	const [initialValues, setInitialValues] = useState({
		temp_id: null,
		abha_id: "",
		p_abha_address: "",
		abha_resource: "3",
		abha_creation_method: "",
		NameOfPerson: "",
		MobileNo: "",
		Email: "",
		Gender: "M",
		MaritalStatus: "",
		SonDaughterOfWifeOf: "",
		BloodGroupID: "",
		category: "",
		is_rural_urban: "",
		taluka_id: null,
		urban_id: null,
		village_id: null,
		block_id: null,
		Address: "",
		age_dob: "dob",
		Year: "",
		month: "",
		day: "",
		dob: "",
		state_id: null,
		district_id: null,
		healthfacilitytype_id: null,
		health_facility_id: null,
		registration_date: new Date().toISOString().split("T")[0],
		entry_from: "nhsrc",
		profile_pic: [{ AttachmentFileType: "jpeg", Base64Data: "" }],
		kyc_pic: [{ AttachmentFileType: "png", Base64Data: "" }],
	});

	// Fetch master data from API
	const getMasterData = async (path, masterType) => {
		setIsLoading(true);
		try {

			const response = await path;

			const dataToSet = response?.data?.ResultType || response?.ResultType || response?.data;

			if (dataToSet) {
				setMasterData((previousState) => ({
					...previousState,
					[masterType]: dataToSet,
				}));
				return dataToSet;
			}
		} catch (error) {
			console.log("Error: ", error);

		} finally {
			setIsLoading(false);
		}
	};

	const formatDate = (dateValue) => {

		if (!dateValue) return "";

		// Already YYYY-MM-DD
		if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
			return dateValue;
		}

		// Replace / and . with -
		const cleanDate = dateValue.replace(/[/.]/g, "-");

		const parts = cleanDate.split("-");

		let day, month, year;

		// DD-MM-YYYY
		if (parts[0].length === 2) {
			[day, month, year] = parts;
		}

		// YYYY-MM-DD
		else if (parts[0].length === 4) {
			[year, month, day] = parts;
		}

		else {
			const date = new Date(dateValue);

			if (isNaN(date)) return "";

			year = date.getFullYear();
			month = String(date.getMonth() + 1).padStart(2, "0");
			day = String(date.getDate()).padStart(2, "0");
		}

		return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
	};

	useEffect(() => {
		const userDetails = localStorage.getItem("SICKLE_loggedinUserdetails");
		const BeneficiaryData = localStorage.getItem("beneficiaryDetails");
		console.log("BeneficiaryData: ", BeneficiaryData);
		let loggedinUserdetails = null;
		let beneficiaryData = null;
		try {
			if (userDetails) loggedinUserdetails = JSON.parse(userDetails);
		} catch (e) {
			console.error("Failed to parse user details", e);
		}
		try {
			if (BeneficiaryData) beneficiaryData = JSON.parse(BeneficiaryData);
		} catch (e) {
			console.error("Failed to parse beneficiary data", e);
		}

		if (loggedinUserdetails) {
			setInitialValues((prev) => ({
				...prev,
				health_facility_id: Number(loggedinUserdetails?.facilityId) || null,
				healthfacilitytype_id: Number(loggedinUserdetails?.health_facility_type) || null,
			}));
		}
		if (beneficiaryData) {
			if (beneficiaryData?.abhaNumber) {
				setIsAbhaVerified(true);
			}
			setInitialValues((prev) => ({
				...prev,
				NameOfPerson: beneficiaryData?.name,
				Gender: beneficiaryData?.gender == "Male" ? "M" : beneficiaryData?.gender == "Female" ? "F" : "T",
				dob: formatDate(beneficiaryData?.dob),
				Address: beneficiaryData?.address,
				MobileNo: beneficiaryData?.mobileOriginal,
				state_id: Number(beneficiaryData?.statelgd) || null,
				district_id: Number(beneficiaryData?.districtlgd) || null,
				abha_id: beneficiaryData?.abhaNumber,
				p_abha_address: beneficiaryData?.preferredAbhaAddress,
				profile_pic: [{ AttachmentFileType: "jpeg", Base64Data: beneficiaryData?.profilePhoto || "" }],
				kyc_pic: [{ AttachmentFileType: "jpeg", Base64Data: beneficiaryData?.originalKycPhoto || "" }]
			}));
		}

		getMasterData(sickleAPI.getState(), "stateMaster");
		getMasterData(sickleAPI.getBloodGroups(), "bloodGroupMaster");
		if (beneficiaryData?.statelgd) {
			getMasterData(sickleAPI.getDistrict({ state_id: beneficiaryData?.statelgd }), "districtMaster");
		}
		if (beneficiaryData?.statelgd && beneficiaryData?.districtlgd) {
			getMasterData(sickleAPI.getBlock({ state_id: beneficiaryData?.statelgd, district_id: beneficiaryData?.districtlgd }), "blockMaster");
		}
		if (beneficiaryData?.statelgd && beneficiaryData?.districtlgd) {
			getMasterData(sickleAPI.getUrban({ state_id: beneficiaryData?.statelgd, district_id: beneficiaryData?.districtlgd }), "urbanMaster");
		}
	}, []);

	// ================= HELPERS =================
	const generateRandomNumber = (length) => {
		let result = "";
		const characters = "0123456789";
		for (let i = 0; i < length; i++) {
			result += characters.charAt(Math.floor(Math.random() * characters.length));
		}
		return result;
	};

	const getDOBDetails = ({
		dobValue = null,
		age_year = null,
		age_month = null,
		age_day = null,
	}) => {

		const today = new Date();

		// ================= IF DOB IS PASSED =================

		if (dobValue) {

			const date = new Date(dobValue);

			if (isNaN(date)) {
				return {
					age_year: null,
					age_month: null,
					age_day: null,
					dob: null,
				};
			}

			let years = today.getFullYear() - date.getFullYear();
			let months = today.getMonth() - date.getMonth();
			let days = today.getDate() - date.getDate();

			// Handle negative days
			if (days < 0) {

				months--;

				const previousMonth = new Date(
					today.getFullYear(),
					today.getMonth(),
					0
				);

				days += previousMonth.getDate();
			}

			// Handle negative months
			if (months < 0) {
				years--;
				months += 12;
			}

			return {
				age_year: years,
				age_month: months,
				age_day: days,
				dob: dobValue,
			};
		}

		// ================= IF AGE IS PASSED =================

		if (
			age_year !== null ||
			age_month !== null ||
			age_day !== null
		) {

			const dobDate = new Date(
				today.getFullYear() - (Number(age_year) || 0),
				today.getMonth() - (Number(age_month) || 0),
				today.getDate() - (Number(age_day) || 0)
			);

			const formattedDOB = dobDate.toISOString().split("T")[0];

			return {
				age_year: Number(age_year) || 0,
				age_month: Number(age_month) || 0,
				age_day: Number(age_day) || 0,
				dob: formattedDOB,
			};
		}

		// ================= DEFAULT =================

		return {
			age_year: null,
			age_month: null,
			age_day: null,
			dob: null,
		};
	};

	// ================= SUBMIT =================
	const handleSubmit = async (values, { setSubmitting }) => {
		try {
			setIsLoading(true);
			let dobDetails = getDOBDetails({
				dobValue: values.age_dob === "dob" ? values.dob : null,
				age_year: values.age_dob === "age" ? values.Year : null,
				age_month: values.age_dob === "age" ? values.month : null,
				age_day: values.age_dob === "age" ? values.day : null,
			});
			// if (values.age_dob === "dob" && values.dob) {
			// 	dobDetails = getDOBDetails(values.dob);
			// }
			console.log("values: ", values);
			const payload = {
				patient_data: [
					{
						temp_id: generateRandomNumber(16),
						abha_id: values.abha_id,
						p_abha_address: values.p_abha_address,
						abha_resource: "",
						abha_creation_method: "",
						state_id: values.state_id,
						district_id: values.district_id,
						is_rural_urban: values.is_rural_urban,
						taluka_id: values.taluka_id,
						urban_id: values.urban_id,
						village_id: values.village_id,
						block_id: values.block_id,
						health_facility_id: values.health_facility_id,
						healthfacilitytype_id: values.healthfacilitytype_id,
						NameOfPerson: values.NameOfPerson,
						Gender: values.Gender || "",
						MaritalStatus: values.MaritalStatus,
						SonDaughterOfWifeOf: values.SonDaughterOfWifeOf,
						BloodGroupID: Number(values.BloodGroupID),
						Year: Number(dobDetails.age_year),
						month: Number(dobDetails.age_month),
						day: Number(dobDetails.age_day),
						dob: dobDetails.dob,
						Address: values.Address,
						category: Number(values.category),
						MobileNo: Number(values.MobileNo),
						Email: values.Email || "",
						registration_date: values.registration_date,
						InchargeMobile: String(values.MobileNo),
						adhar_consent_given: "0",
						adhar_consent_date: null,
						data_received_online: "T",
						entry_from: values.entry_from,
						profile_pic: values.profile_pic,
						kyc_pic: values.kyc_pic,
						qr_code: [{ AttachmentFileType: "png", Base64Data: "" }],
					},
				],
			};
			console.log("Payload: ", payload);
			const response = await sickleAPI.registerBeneficiary(payload);

			if (response?.success === true || response?.success === "true" || response?.status === 200 || response?.status === "success" || response?.message?.toLowerCase().includes("success")) {
				toast.success(response?.message || "Registration successful");
				setTimeout(() => {
					window.location.reload();
				}, 3000);
			} else {
				toast.error(response?.message || "Registration failed");
			}
		} catch (error) {
			const errorMessage =
				error?.response?.data?.message ||
				error?.response?.data?.Message ||
				error?.message ||
				"Something went wrong";
			toast.error(errorMessage);
		} finally {
			setSubmitting(false);
			setIsLoading(false);
		}
	};

	// ================= UI =================
	return (
		<>
			{isLoading && <Loader />}
			{/* <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}> */}
				{/* HEADER */}
				<div className="patientDetailsTitleHeader-h d-flex justify-content-between align-items-center mb-3">
					<div className="leftHeaderBar d-flex align-items-center gap-2">
						<span><img src={"/images/Sickel.svg"} alt="" style={{ width: "45px" }} /></span>
						<h4 className="mb-0 fw-bold">Sickle Cell Registration</h4>
					</div>
					<div className="closeDive sickleclosereg" style={{ cursor: "pointer" }} onClick={() => onClose('handleCloseSickleregistration')}>
						<img src={"/images/closing.svg"} alt="Close" />
					</div>
				</div>

				{/* FORM FIELDS */}
				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={handleSubmit}
					enableReinitialize
				>
					{({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
						<Form>
							<Grid container spacing={3} mb={5}>
								{/* MOBILE */}
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										fullWidth
										label={<span>Mobile Number <span style={{ color: "red" }}>*</span></span>}
										name="MobileNo"
										value={values?.MobileNo}
										onChange={handleChange}
										onBlur={handleBlur}
										variant="outlined"
										disabled={true}
										error={touched?.MobileNo && Boolean(errors?.MobileNo)}
										helperText={touched?.MobileNo && errors?.MobileNo}
									/>
								</Grid>

								{/* PATIENT NAME */}
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										fullWidth
										label={<span>Patient Name <span style={{ color: "red" }}>*</span></span>}
										name="NameOfPerson"
										value={values?.NameOfPerson}
										onChange={handleChange}
										onBlur={handleBlur}
										variant="outlined"
										disabled={true}
										error={touched?.NameOfPerson && Boolean(errors?.NameOfPerson)}
										helperText={touched?.NameOfPerson && errors?.NameOfPerson}
									/>
								</Grid>

								{/* EMAIL */}
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										fullWidth
										label="Email"
										type="email"
										name="Email"
										value={values?.Email}
										onChange={handleChange}
										onBlur={handleBlur}
										variant="outlined"
										error={touched?.Email && Boolean(errors?.Email)}
										helperText={touched?.Email && errors?.Email}
									/>
								</Grid>
							</Grid>

							<Grid container spacing={3}>
								{/* GENDER */}
								<Grid size={{ xs: 12, md: 4 }}>
									<Typography mb={1}>Gender <span style={{ color: "red" }}>*</span></Typography>
									<RadioGroup
										row
										name="Gender"
										value={values?.Gender}
										onChange={handleChange}
										disabled={true}
									>
										<FormControlLabel disabled={true} checked={values?.Gender === "M"} value="M" control={<Radio />} label="Male" />
										<FormControlLabel disabled={true} checked={values?.Gender === "F"} value="F" control={<Radio />} label="Female" />
										<FormControlLabel disabled={true} checked={values?.Gender === "T"} value="T" control={<Radio />} label="Transgender" />
									</RadioGroup>
									{touched?.Gender && errors?.Gender && (
										<Typography color="error" variant="caption">
											{errors?.Gender}
										</Typography>
									)}
								</Grid>

								{/* AGE / DOB */}
								<Grid size={{ xs: 12, md: 4 }}>
									<Typography mb={1}>Date Of Birth / Age <span style={{ color: "red" }}>*</span></Typography>
									<RadioGroup
										row
										name="age_dob"
										value={values?.age_dob}
										onChange={(e) => {
											setFieldValue("age_dob", e.target.value);
											if (e.target.value === "age") {
												setFieldValue("dob", "");
											} else {
												setFieldValue("Year", "");
												setFieldValue("month", "");
												setFieldValue("day", "");
											}
										}}
									>
										<FormControlLabel disabled={true} value="dob" control={<Radio />} label="Date of Birth" />
										<FormControlLabel disabled={true} value="age" control={<Radio />} label="Age" />
									</RadioGroup>
									{touched?.age_dob && errors?.age_dob && (
										<Typography color="error" variant="caption">
											{errors?.age_dob}
										</Typography>
									)}
								</Grid>

								{values?.age_dob === "dob" && (
									<Grid size={{ xs: 12, md: 4 }}>
										<TextField
											fullWidth
											type="date"
											label={<span>Date Of Birth <span style={{ color: "red" }}>*</span></span>}
											name="dob"
											disabled={true}
											InputLabelProps={{ shrink: true }}
											value={values?.dob}
											onChange={handleChange}
											onBlur={handleBlur}
											error={touched?.dob && Boolean(errors?.dob)}
											helperText={touched?.dob && errors?.dob}
										/>
									</Grid>
								)}

								{values?.age_dob === "age" && (
									<>
										<Grid size={{ xs: 12, md: 2 }}>
											<TextField
												fullWidth
												label={<span>Year <span style={{ color: "red" }}>*</span></span>}
												name="Year"
												value={values?.Year}
												onChange={handleChange}
												onBlur={handleBlur}
												error={touched?.Year && Boolean(errors?.Year)}
												helperText={touched?.Year && errors?.Year}
											/>
										</Grid>
										<Grid size={{ xs: 12, md: 1 }}>
											<TextField
												fullWidth
												label={<span>Month <span style={{ color: "red" }}>*</span></span>}
												name="month"
												value={values?.month}
												onChange={handleChange}
												onBlur={handleBlur}
												error={touched?.month && Boolean(errors?.month)}
												helperText={touched?.month && errors?.month}
											/>
										</Grid>
										<Grid size={{ xs: 12, md: 1 }}>
											<TextField
												fullWidth
												label={<span>Day <span style={{ color: "red" }}>*</span></span>}
												name="day"
												value={values?.day}
												onChange={handleChange}
												onBlur={handleBlur}
												inputProps={{ inputMode: "numeric", maxLength: 2 }}
												error={touched?.day && Boolean(errors?.day)}
												helperText={touched?.day && errors?.day}
											/>
										</Grid>
									</>
								)}

								{/* MARITAL STATUS */}
								<Grid size={{ xs: 12, md: 4 }}>
									<Typography mb={1}>Marital Status <span style={{ color: "red" }}>*</span></Typography>
									<RadioGroup
										row
										name="MaritalStatus"
										value={values?.MaritalStatus}
										onChange={handleChange}
									>
										<FormControlLabel value="M" control={<Radio />} label="Married" />
										<FormControlLabel value="U" control={<Radio />} label="Unmarried" />
									</RadioGroup>
									{touched?.MaritalStatus && errors?.MaritalStatus && (
										<Typography color="error" variant="caption">
											{errors?.MaritalStatus}
										</Typography>
									)}
								</Grid>

								{/* FATHER / HUSBAND NAME */}
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										fullWidth
										label={<span>Father/Husband Name <span style={{ color: "red" }}>*</span></span>}
										name="SonDaughterOfWifeOf"
										value={values?.SonDaughterOfWifeOf}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched?.SonDaughterOfWifeOf && Boolean(errors?.SonDaughterOfWifeOf)}
										helperText={touched?.SonDaughterOfWifeOf && errors?.SonDaughterOfWifeOf}
									/>
								</Grid>

								{/* ADDRESS */}
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										fullWidth
										multiline
										rows={1}
										label={<span>Address <span style={{ color: "red" }}>*</span></span>}
										name="Address"
										disabled={isAbhaVerified}
										value={values?.Address}
										onChange={handleChange}
										onBlur={handleBlur}
										error={touched?.Address && Boolean(errors?.Address)}
										helperText={touched?.Address && errors?.Address}
									/>
								</Grid>

								{/* STATE */}
								<Grid size={{ xs: 12, md: 4 }}>
									<FormControl fullWidth error={touched?.state_id && Boolean(errors?.state_id)}>
										<InputLabel>State <span style={{ color: "red" }}>*</span></InputLabel>
										<Select
											name="state_id"
											value={values?.state_id || ""}
											label={<span>State <span style={{ color: "red" }}>*</span></span>}
											disabled={isAbhaVerified}
											onChange={(e) => {
												handleChange(e);
												setFieldValue("district_id", "");
												setFieldValue("block_id", "");
												setFieldValue("urban_id", "");
												setFieldValue("village_id", "");
												setFieldValue("is_rural_urban", "");
												if (e.target.value) {
													getMasterData(sickleAPI.getDistrict({ state_id: e.target.value }), "districtMaster");
												} else {
													setMasterData((prev) => ({ ...prev, districtMaster: [] }));
												}
											}}
											onBlur={handleBlur}
										>
											<MenuItem value="">Select</MenuItem>
											{masterData?.stateMaster?.sort((a, b) => a?.state_name.localeCompare(b?.state_name))?.map((item, index) => (
												<MenuItem key={index} value={item?.state_lgd_code}>
													{item?.state_name}
												</MenuItem>
											))}
										</Select>
										{touched?.state_id && errors?.state_id && (
											<Typography color="error" variant="caption" sx={{ ml: 2 }}>
												{errors?.state_id}
											</Typography>
										)}
									</FormControl>
								</Grid>
								{values?.state_id !== "" && (
									<>
										{/* DISTRICT */}
										<Grid size={{ xs: 12, md: 4 }}>
											<FormControl fullWidth error={touched?.district_id && Boolean(errors?.district_id)}>
												<InputLabel>District <span style={{ color: "red" }}>*</span></InputLabel>
												<Select
													name="district_id"
													value={values?.district_id || ""}
													disabled={isAbhaVerified}
													label={<span>District <span style={{ color: "red" }}>*</span></span>}
													onChange={(e) => {
														handleChange(e);
														setFieldValue("block_id", "");
														setFieldValue("urban_id", "");
														setFieldValue("is_rural_urban", "");
														if (e.target.value) {
															getMasterData(sickleAPI.getBlock({ state_id: values?.state_id, district_id: e.target.value }), "blockMaster");
															getMasterData(sickleAPI.getUrban({ state_id: values?.state_id, district_id: e.target.value }), "urbanMaster");
														} else {
															setMasterData((prev) => ({ ...prev, blockMaster: [], urbanMaster: [] }));
														}
													}}
													onBlur={handleBlur}
												>
													<MenuItem value="">Select</MenuItem>
													{masterData?.districtMaster?.sort((a, b) => a?.district_name?.localeCompare(b?.district_name))?.map((item, index) => (
														<MenuItem key={index} value={item?.district_lgd_code || item?.district_id}>
															{item?.district_name}
														</MenuItem>
													))}
												</Select>
												{touched?.district_id && errors?.district_id && (
													<Typography color="error" variant="caption" sx={{ ml: 2 }}>
														{errors?.district_id}
													</Typography>
												)}
											</FormControl>
										</Grid>
									</>
								)}
								{/* Rural / Urban */}
								<Grid size={{ xs: 12, md: 4 }}>
									<Typography mb={1}>Rural / Urban <span style={{ color: "red" }}>*</span></Typography>
									<RadioGroup
										row
										name="is_rural_urban"
										value={values?.is_rural_urban}
										onChange={(e) => {
											handleChange(e);
											setFieldValue("village_id", "");
											setFieldValue("block_id", "");
											setFieldValue("urban_id", "");
										}}
									>
										<FormControlLabel value="R" control={<Radio />} label="Rural" />
										<FormControlLabel value="U" control={<Radio />} label="Urban" />
									</RadioGroup>
									{touched?.is_rural_urban && errors?.is_rural_urban && (
										<Typography color="error" variant="caption">
											{errors?.is_rural_urban}
										</Typography>
									)}
								</Grid>
								{values?.is_rural_urban === "R" && (
									<>
										{/* Block */}
										<Grid size={{ xs: 12, md: 4 }}>
											<FormControl
												fullWidth
												error={touched?.block_id && Boolean(errors?.block_id)}
											>
												<InputLabel>
													Block <span style={{ color: "red" }}>*</span>
												</InputLabel>

												<Select
													name="block_id"
													value={values?.block_id || ""}
													label="Block *"
													onChange={(e) => {
														handleChange(e);
														setFieldValue("village_id", "");
														if (e.target.value) {
															getMasterData(sickleAPI.getVillage({ state_id: values?.state_id, district_id: values?.district_id, block_id: e.target.value }), "villageMaster");
														} else {
															setMasterData((prev) => ({ ...prev, villageMaster: [] }));
														}
													}}
													onBlur={handleBlur}
												>
													<MenuItem value="">Select</MenuItem>
													{masterData?.blockMaster?.sort((a, b) => a?.block_name?.localeCompare(b?.block_name))?.map((item, index) => (
														<MenuItem key={index} value={item?.block_lgd_code || item?.block_id}>
															{item?.block_name}
														</MenuItem>
													))}
												</Select>

												{touched?.block_id && errors?.block_id && (
													<Typography
														color="error"
														variant="caption"
														sx={{ ml: 2 }}
													>
														{errors?.block_id}
													</Typography>
												)}
											</FormControl>
										</Grid>

										{/* Village */}
										{values?.block_id && (
											<Grid size={{ xs: 12, md: 4 }}>
												<FormControl
													fullWidth
													error={
														touched?.village_id &&
														Boolean(errors?.village_id)
													}
												>
													<InputLabel>
														Village{" "}
														<span style={{ color: "red" }}>*</span>
													</InputLabel>

													<Select
														name="village_id"
														value={values?.village_id || ""}
														label="Village *"
														onChange={handleChange}
														onBlur={handleBlur}
													>
														<MenuItem value="">Select</MenuItem>
														{masterData?.villageMaster?.sort((a, b) => a?.village_name?.localeCompare(b?.village_name))?.map((item, index) => (
															<MenuItem key={index} value={item?.village_lgd_code || item?.village_id}>
																{item?.village_name}
															</MenuItem>
														))}
													</Select>

													{touched?.village_id &&
														errors?.village_id && (
															<Typography
																color="error"
																variant="caption"
																sx={{ ml: 2 }}
															>
																{errors?.village_id}
															</Typography>
														)}
												</FormControl>
											</Grid>
										)}
									</>
								)}
								{
									values?.is_rural_urban === "U" && (
										<>
											{/* Town/Municipality */}
											<Grid size={{ xs: 12, md: 4 }}>
												<FormControl fullWidth error={touched?.district_id && Boolean(errors?.district_id)}>
													<InputLabel>Town/Municipality <span style={{ color: "red" }}>*</span></InputLabel>
													<Select
														name="urban_id"
														value={values?.urban_id || ""}
														label={<span>Town/Municipality <span style={{ color: "red" }}>*</span></span>}
														onChange={handleChange}
														onBlur={handleBlur}
													>
														<MenuItem value="">Select</MenuItem>
														{masterData?.urbanMaster?.sort((a, b) => a?.ulb_name?.localeCompare(b?.ulb_name))?.map((item, index) => (
															<MenuItem key={index} value={item?.ulb_lgd_code || item?.urban_id}>
																{item?.ulb_name}
															</MenuItem>
														))}
													</Select>
													{touched?.urban_id && errors?.urban_id && (
														<Typography color="error" variant="caption" sx={{ ml: 2 }}>
															{errors?.urban_id}
														</Typography>
													)}
												</FormControl>
											</Grid>
										</>
									)}

								{/* CATEGORY */}
								<Grid size={{ xs: 12, md: 4 }}>
									<Typography mb={1}>Category <span style={{ color: "red" }}>*</span></Typography>
									<RadioGroup
										row
										name="category"
										value={values?.category}
										onChange={handleChange}
									>
										<FormControlLabel value="1" control={<Radio />} label="General" />
										<FormControlLabel value="2" control={<Radio />} label="SC" />
										<FormControlLabel value="3" control={<Radio />} label="ST" />
										<FormControlLabel value="4" control={<Radio />} label="BC" />
										<FormControlLabel value="5" control={<Radio />} label="PVTG" />
									</RadioGroup>
									{touched?.category && errors?.category && (
										<Typography color="error" variant="caption">
											{errors?.category}
										</Typography>
									)}
								</Grid>

								{/* BLOOD GROUP */}
								<Grid size={{ xs: 12, md: 4 }}>
									<FormControl fullWidth error={touched?.BloodGroupID && Boolean(errors?.BloodGroupID)}>
										<InputLabel>Blood Group <span style={{ color: "red" }}>*</span></InputLabel>
										<Select
											name="BloodGroupID"
											value={values?.BloodGroupID}
											label={<span>Blood Group <span style={{ color: "red" }}>*</span></span>}
											onChange={handleChange}
											onBlur={handleBlur}
										>
											<MenuItem value="">Select</MenuItem>
											{masterData?.bloodGroupMaster?.BloodGroups?.sort((a, b) => a?.bg_name?.localeCompare(b?.bg_name))?.map((item, index) => (
												<MenuItem key={index} value={item?.id}>
													{item?.bg_name}
												</MenuItem>
											))}
										</Select>
										{touched?.BloodGroupID && errors?.BloodGroupID && (
											<Typography color="error" variant="caption" sx={{ ml: 2 }}>
												{errors?.BloodGroupID}
											</Typography>
										)}
									</FormControl>
								</Grid>

								{/* REGISTRATION DATE */}
								<Grid size={{ xs: 12, md: 4 }}>
									<TextField
										fullWidth
										type="date"
										label={<span>Registration Date <span style={{ color: "red" }}>*</span></span>}
										name="registration_date"
										value={values?.registration_date}
										disabled={true}
									/>
								</Grid>

								{/* SUBMIT BUTTON */}
								<Grid size={{ xs: 12, md: 12 }}>
									<Box display="flex" justifyContent="flex-end" ref={targetRef}>
										<Button
											variant="contained"
											size="large"
											type="submit"
											disabled={isSubmitting}
										>
											Save
										</Button>
									</Box>
								</Grid>
							</Grid>
						</Form>
					)}
				</Formik>
			{/* </Paper> */}
		</>
	);
};
